import React from 'react'
import PropTypes from 'prop-types'
import {Avatar, Box, Text, Flex, CircularProgress} from '@chakra-ui/react'
import {CallToAction, PENDING_STATUSES} from './CallToAction'

const StepIcon = ({icon, ...props}) => {
  return (
    <Flex {...props} direction="column" align="center" pr="sm">
      <Avatar
        w="40px"
        h="40px"
        bg="mld.neutral.800"
        size="lg"
        color="neutral.white"
        icon={icon}
      />
    </Flex>
  )
}

StepIcon.propTypes = {
  icon: PropTypes.element.isRequired,
}

const VerticalStepper = ({title, subtitle, steps, currentStep, className}) => {
  const [incompletedSteps, completedSteps] = steps.reduce(
    (arr, step) => {
      if (step.status === 'completed') {
        arr[1].push(step)
      } else {
        arr[0].push(step)
      }

      return arr
    },
    [[], []],
  )

  const percentage = (completedSteps.length / steps.length) * 100

  const sortedSteps = [...incompletedSteps, ...completedSteps]
  const allStepsCompleted = sortedSteps.every(
    (step) => step.status === 'completed',
  )

  const currentStepIndex = sortedSteps.findIndex(
    (step) => step.name === currentStep,
  )

  return (
    <Box className={className}>
      <Flex
        justify="space-between"
        align="center"
        pb={{base: 'md', md: 'xs'}}
        gap="sm"
      >
        <Flex direction="column" justify="space-between" gap="sm">
          <Text textStyle="headline5" textTransform="uppercase">
            {title}
          </Text>
          <Text textStyle="headline3">{subtitle}</Text>
        </Flex>

        {!allStepsCompleted && (
          <CircularProgress
            color="mld.neutral.1000"
            value={percentage}
            trackColor="mld.neutral.200"
            size="45px"
            thickness="10px"
          />
        )}
      </Flex>
      <Flex flexDirection="column">
        {sortedSteps.map((step, index) => {
          const completedStep = step.status === 'completed'
          // A pending step is waiting on an external result, not locked, so
          // it keeps full opacity to stay visually distinct from the locked
          // steps below it.
          const pendingStep = PENDING_STATUSES.includes(step.status)
          // A step may drive its own enablement via an `active` flag, so that
          // several steps can be unlocked at once (the PoD onboarding stepper
          // unlocks steps in pairs). When absent, fall back to marking only the
          // current step active.
          const activeStep =
            step.active !== undefined
              ? step.active
              : currentStepIndex === index && !completedStep

          return (
            <Flex
              key={step.name}
              justifyContent="space-between"
              alignItems="center"
              minH="95px"
              backgroundColor="white"
              flexWrap="wrap"
              px="xs"
              py={{base: 'md', md: 'xs'}}
              borderTop="0.5px solid"
              borderColor="mld.neutral.800"
              opacity={activeStep || pendingStep ? null : 0.4}
              gap={{base: 'sm', md: 'xxs'}}
              data-testid={
                completedStep
                  ? `completed_step_${step.name}`
                  : `step_${step.name}`
              }
            >
              {/* Keep the icon and text as one unit so a long subtitle wraps
                  within the text column rather than pushing the icon onto its
                  own line. Only the call to action wraps below. */}
              <Flex align="center" flex="1" minW={0}>
                <StepIcon
                  icon={step.icon}
                  previousStep={index < currentStepIndex}
                  activeStep={activeStep}
                  showDivider={index < steps.length - 1}
                  display={{
                    base: completedStep ? 'none' : 'block',
                    md: 'block',
                  }}
                />
                <Flex direction="column" gap="xxs" flexGrow={1} minW={0}>
                  <Text textStyle="body-intro">{step.title}</Text>
                  {step.subtitle && (
                    <Text textStyle="body-copy">
                      {step.subtitle[step.status] || step.subtitle.default}
                    </Text>
                  )}
                  {step.notification}
                </Flex>
              </Flex>
              <CallToAction
                status={step.status}
                action={
                  step.action ? {...step.action, isDisabled: !activeStep} : null
                }
                order={{base: completedStep ? -1 : null, md: 2}}
              />
            </Flex>
          )
        })}
      </Flex>
    </Box>
  )
}

VerticalStepper.propTypes = {
  title: PropTypes.string.isRequired,
  subtitle: PropTypes.string.isRequired,
  steps: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string.isRequired,
      status: PropTypes.string.isRequired,
      // Optional per-step enablement. When set, it overrides the
      // single-current-step fallback so multiple steps can be unlocked at once.
      active: PropTypes.bool,
      icon: PropTypes.element.isRequired,
      title: PropTypes.string.isRequired,
      subtitle: PropTypes.shape({
        default: PropTypes.string.isRequired,
        incomplete: PropTypes.string,
        emailed: PropTypes.string,
        reviewing: PropTypes.string,
        declined: PropTypes.string,
        completed: PropTypes.string,
      }),
      // Optional inline node rendered under the subtitle (e.g. error details).
      notification: PropTypes.node,
      action: PropTypes.shape({
        label: PropTypes.string.isRequired,
        onClick: PropTypes.func.isRequired,
      }),
    }),
  ).isRequired,
  currentStep: PropTypes.string,
  className: PropTypes.string,
}

VerticalStepper.defaultProps = {
  currentStep: null,
  className: '',
}

export default VerticalStepper
